import './styles/FilterBar.scss';

import AntdForm                from 'antd/lib/form';
import Dropdown                from 'antd/lib/dropdown';
import React                   from 'react';
import { CloseCircleOutlined } from '@ant-design/icons';
import { PlusOutlined }        from '@ant-design/icons';
import { ClearOutlined }       from '@ant-design/icons';
import View                    from '@widesk-ui/components/View';
import Button                  from '@widesk-ui/components/Button';
import useTheme                from '@widesk-ui/hooks/useTheme';
import Input                   from '@widesk-ui/components/form/Input';
import { WideskInputRef }      from '@widesk-ui/components/form/Input';

export type InitialFields = Record<string, InitialField>;

export type InitialField = {
	defaultValue?: unknown;
	label?: string,
	placeholder?: string,
	element: React.ReactElement<FieldProps>;
	value?: unknown;
}

export type Field = InitialField & {
	name: string;
	hidden: boolean;
	forceDisplay?: boolean;
	displayRight?: boolean;
	submitOnChange?: boolean;
}

export type FilterBarProps<T extends InitialFields> = {
	initialFields: T;
	onSubmit: (values: { [Property in keyof T]: unknown }) => void;
}

export default function FilterBar<T extends InitialFields>(props: FilterBarProps<T>) {
	const [loading, setLoading] = React.useState(false);

	const [fields, setFields] = React.useState<Field[]>(
		Object.entries(props.initialFields).map(([name, initialField]) => {
			return { hidden: typeof initialField.value === 'undefined', name, ...initialField };
		}),
	);

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [_isMounted, setIsMounted] = React.useState(false);

	const dropdownMenuId = React.useMemo(() => `dropdown-menu-${(Math.random() + 1).toString(36)}`, []);

	// Permet de gérer l'état "disabled" du bouton "supprimer les filtres" au premier chargement
	React.useEffect(() => setIsMounted(true), []);

	const theme = useTheme();
	const [form] = AntdForm.useForm();

	const searchFilterRef = React.useRef<WideskInputRef>(null);

	const toggleField = (field: Field) => {
		field.hidden = !field.hidden;
		setFields([...fields]);
	};

	if (!fields.length) return null;

	const dropdownFields = fields.filter(field => !field.forceDisplay);
	const displayedFields = fields.filter(field => !field.hidden || field.forceDisplay);
	const leftDisplayedFields = displayedFields.filter(field => !field.displayRight);
	const rightDisplayedFields = displayedFields.filter(field => field.displayRight);

	const fieldsValue = form.getFieldsValue();
	const values = Object.values(fieldsValue).filter(v => typeof v !== 'undefined');
	const removeFields = () => {
		setFields(prevFields => [...prevFields.map(f => ({ ...f, hidden: true }))]);
		form.setFieldsValue(fields.reduce<any>((acc, field) => {
			acc[field.name] = field.defaultValue || undefined;
			return acc;
		}, {}));

		setTimeout(() => form.submit());
	};

	const onSubmit = async (values: any) => {
		try {
			setLoading(true);

			if (props.onSubmit) {
				await props.onSubmit(values);
			}
		} finally {
			setLoading(false);
		}
	};

	return (
		<AntdForm form={form} onFinish={onSubmit}>
			<View
				bg={theme.colorBgContainer}
				className="widesk-filter-bar"
				minHeight={54}
				centerV
				separatorB
				paddingH={theme.marginLG}
				paddingV={theme.marginSM}
			>
				<View row wrap gap={10}>
					{!!leftDisplayedFields.length && (
						<View row centerV gap={10} wrap>
							<View row gap={theme.marginSM} wrap>
								{leftDisplayedFields.map(field => {
									return (
										<FilterField
											key={field.name}
											field={field}
											onChange={() => form.submit()}
											onClose={() => toggleField(field)}
										/>
									);
								})}
							</View>
						</View>
					)}

					<View flex={1} spread row centerV gap={10}>
						{!!dropdownFields.length && (
							<Dropdown
								arrow
								menu={{
									id: dropdownMenuId,
									style: { maxHeight: '55vh'},
									items: [
										{
											type: 'group',
											key: 0,
											label:
												<Input
													allowClear
													autoFocus
													autoComplete="off"
													placeholder={"Chercher un filtre"}
													onPressEnter={e => {
														e.preventDefault();
														e.stopPropagation();

														const dropdown = document.getElementById(dropdownMenuId) as HTMLElement;
														const dropdownItems = dropdown
															.querySelectorAll('li[role="menuitem"]') as NodeListOf<HTMLLIElement>;

														if (!dropdownItems) {
															return;
														}

														for (let i = 0; i < dropdownItems.length; i++) {
															const li = dropdownItems.item(i) as HTMLLIElement;
															if (
																li.getAttribute('aria-hidden') !== 'true'
																&& li.getAttribute('aria-disabled') !== 'true'
															) {
																return li.click();
															}
														}
													}}
													onChange={(e: any) => {
														const dropdown = document.getElementById(dropdownMenuId) as HTMLElement;
														const dropdownItems = dropdown
															.querySelectorAll('li[role="menuitem"]') as NodeListOf<HTMLLIElement>;
														const noSearchResultItem = dropdown
															.querySelector('li[role="presentation"]:last-child') as HTMLLIElement;

														const lowerValue = e.target?.value.toLowerCase()
															.normalize("NFD")
															.replace(/\p{Diacritic}/gu, "");

														let hidden = 0;
														dropdownItems.forEach(li => {
															const liText = li.textContent?.toLowerCase()
																.normalize("NFD")
																.replace(/\p{Diacritic}/gu, "");

															if (!lowerValue || liText?.indexOf(lowerValue) !== -1) {
																li.ariaHidden = 'false';
																li.style.display = 'flex';
																return;
															}

															li.ariaHidden = 'true';
															li.style.display = 'none';
															hidden++;
														});

														if (dropdownItems.length === hidden && noSearchResultItem.style.display === 'none') {
															noSearchResultItem.style.display = 'flex';
														}
														if (dropdownItems.length !== hidden && noSearchResultItem.style.display === 'flex') {
															noSearchResultItem.style.display = 'none';
														}
													}}
													ref={searchFilterRef}
												/>,
											style: {
												position: 'sticky',
												top: 0,
												margin: '0 -4px 4px -4px',
												paddingBottom: 4,
												zIndex: 1,
												background: theme.colorBgElevated,
												boxShadow: `0px -4px 0 ${theme.colorBgElevated}`,
												borderBottom: `1px solid ${theme.colorBorderSecondary}`
											},
										},
										...dropdownFields.map(field => ({
											disabled: !field.hidden,
											key: field.name,
											label: field.label,
											onClick: () => toggleField(field),
										})),
										{
											key: 'noSearchResult',
											type: 'group',
											label: 'Aucun filtre trouvé',
											style: { display: 'none', fontStyle: 'italic' },
										},
									],
								}}
								onOpenChange={open => {
									if (!open) {
										return searchFilterRef.current?.clear();
									}
									searchFilterRef.current?.focus();
								}}
								trigger={['click']}
							>
								<Button
									icon={<PlusOutlined />}
									onClick={e => e.preventDefault()}
									type="link"
									style={{ fontWeight: 'bold', padding: 0 }}
								>
									FILTRES
								</Button>
							</Dropdown>
						)}

						<View row gap={10} style={{ marginLeft: 'auto' }}>
							<View row gapH="xxs">
								{rightDisplayedFields.map(field => {
									return (
										<FilterField
											key={field.name}
											field={field}
											onChange={() => form.submit()}
											onClose={() => toggleField(field)}
										/>
									);
								})}
							</View>

							<Button loading={loading} disabled={loading} htmlType="submit">
								Appliquer les filtres
							</Button>

							<Button
								disabled={!values.length}
								onClick={() => removeFields()}
								title="Supprimer les filtres"
							>
								<ClearOutlined />
							</Button>
						</View>
					</View>
				</View>
			</View>
		</AntdForm>
	);
}

type FilterBarItemProps = {
	label?: string;
	placeholder?: string;
	name: string;
	children: React.ReactElement<FieldProps>;
	initialValue?: unknown;
	onChange?: () => void;
}

export function FilterBarItem(props: FilterBarItemProps) {
	const updatedChildren = React.Children.map(props.children, child => {
		if (React.isValidElement(child)) {
			return React.cloneElement(child, {
				autoFocus: true,
				size: 'middle',
				onChange: props.onChange,
				placeholder: props.placeholder || props.label,
			});
		}
		return child;
	});

	return (
		<AntdForm.Item
			name={props.name}
			initialValue={props.initialValue}
		>
			{updatedChildren}
		</AntdForm.Item>
	);
}

const FilterField = ({ field, onChange, onClose }: {
	field: Field;
	onClose: () => void;
	onChange: () => void;
}) => {
	const theme = useTheme();

	return (
		<View row centerV gap={4} className="filter-item" key={field.name}>
			{!!field.label && (
				<View color={theme.colorTextLabel} noWrap>
					{field.label} :
				</View>
			)}

			<FilterBarItem
				label={field.label}
				placeholder={field.placeholder}
				name={field.name}
				initialValue={field.value}
				{...(field.submitOnChange ? { onChange: () => onChange() } : {})}
			>
				{field.element}
			</FilterBarItem>

			{!field.forceDisplay && <CloseCircleOutlined onClick={onClose} />}
		</View>
	);
};